/**
 * Client-side itinerary parser service.
 *
 * Gemini calls have moved to Cloud Functions (functions/src/itineraryParser.ts)
 * so the API key never appears in the browser bundle. This module handles:
 *   - File → base64 conversion (still client-side, no secrets needed)
 *   - Calling the `parseItinerary` and `generateTripName` callable functions
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { ParsedItinerary } from '../types';
import { calendarDayNumber } from '../utils/travelState';
import { itineraryFileType } from '../utils/itineraryFiles';
import { BOOKING_DOC_MAX_BYTES } from '../../functions/src/bookingDocumentCompression.mjs';

export type ItineraryInput =
  | { type: 'text'; content: string }
  | { type: 'file'; base64: string; mimeType: 'application/pdf' | 'image/jpeg' | 'image/png' | 'image/webp' };

export const ITINERARY_FILE_ACCEPT = '.xlsx,.xls,.csv,.pdf,.docx,.jpg,.jpeg,.png,.webp,.txt,.eml';

// ── Callable references ───────────────────────────────────────────────────────

// 10-minute timeout matches the Cloud Function's 300s ceiling + network headroom.
const _parseItineraryCf = httpsCallable<
  { inputs: ItineraryInput[]; homeCurrency?: string },
  ParsedItinerary | null
>(functions, 'parseItinerary', { timeout: 560_000 });

const _generateTripNameCf = httpsCallable<
  { destinations: { city: string; country?: string }[] },
  string | null
>(functions, 'generateTripName', { timeout: 65_000 });

const _generateDestinationDetailsCf = httpsCallable<
  { destinations: { city: string; country?: string | null }[] },
  { emoji: string; blurb: string }[]
>(functions, 'generateDestinationDetails', { timeout: 30_000 });

// ── Convention B: programmatic date pre-computation ───────────────────────────
//
// Convention B spreadsheets have rows like:
//   Date (col A)  | Departure city (col B) | Arrival city (col C) | N nights (col D)
//
// We parse these in code so Gemini never has to do the date arithmetic — it
// consistently added +1 travel day no matter how the prompt was worded.

function _parseDateCell(s: string): Date | null {
  // Strip day-of-week prefix: "Mon 29 Jun 2026" → "29 Jun 2026"
  const clean = s.trim().replace(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)[,.\s]+/i, '').trim();
  const months: Record<string, number> = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  };
  // "25 Jun 2026" or "25-Jun-2026"
  const m1 = clean.match(/^(\d{1,2})[\s\-\/]([A-Za-z]{3})[\s\-\/,]+(\d{4})$/);
  // "Jun 25 2026"
  const m2 = clean.match(/^([A-Za-z]{3})[\s\-\/](\d{1,2})[\s\-\/,]+(\d{4})$/);
  const match = m1 || m2;
  if (!match) return null;
  const month = months[(m1 ? match[2] : match[1]).toLowerCase()];
  if (month === undefined) return null;
  const day = Number(m1 ? match[1] : match[2]);
  const date = `${match[3]}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return calendarDayNumber(date) === null ? null : new Date(`${date}T00:00:00Z`);
}

function _ymd(d: Date): string {
  return `${String(d.getUTCFullYear()).padStart(4, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function _addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setUTCDate(result.getUTCDate() + n);
  return result;
}

/**
 * Detect Convention B spreadsheet rows and return a pre-computed destination
 * date block to prepend to the Gemini prompt, or null if not detected.
 *
 * Convention B row: col A = date | col B = departure city | col C = arrival city | col D = N nights
 *
 * Date logic:
 *   - departure city departureDate  = Date (col A)       — they leave that day
 *   - arrival city  arrivalDate     = Date (col A)       — they arrive that same day (no +1)
 *   - arrival city  departureDate   = arrivalDate + nights
 */
function _precomputeConventionB(rows: string[][]): string | null {
  interface TRow { date: Date; from: string; to: string; nights: number; }
  const travelRows: TRow[] = [];
  const cityRx = /^[a-zA-ZÀ-ÿ\s\/\-\.'']+$/;

  for (const row of rows) {
    const a = String(row[0] ?? '').trim();
    const b = String(row[1] ?? '').trim();
    const c = String(row[2] ?? '').trim();
    const d = String(row[3] ?? '').trim();
    if (!cityRx.test(b) || b.length > 60) continue;
    if (!cityRx.test(c) || c.length > 60) continue;
    // Recognize travel-shaped rows before validation, without mistaking column headers for data.
    if (!/\d/.test(a) && !/\d/.test(d) && !/^(?:[+-]?infinity|nan)\s*nights?$/i.test(d)) continue;
    const date = _parseDateCell(a);
    const nightMatch = d.match(/^(\d+)\s*nights?$/i);
    const nights = nightMatch ? Number(nightMatch[1]) : NaN;
    // A malformed travel row makes this block uncertain; retain raw sheet data instead.
    if (!date || !Number.isSafeInteger(nights) || calendarDayNumber(_ymd(_addDays(date, nights))) === null) return null;
    travelRows.push({ date, from: b, to: c, nights });
  }

  if (travelRows.length < 2) return null; // not enough signal

  // A city can be visited more than once. Only the immediately preceding stay
  // can supply the next departure; never combine dates across return visits.
  const visits: { name: string; arrival?: Date; departure: Date }[] = [];
  for (const tr of travelRows.sort((a, b) => a.date.getTime() - b.date.getTime())) {
    const previous = visits[visits.length - 1];
    if (tr.from.toLowerCase() === tr.to.toLowerCase()) return null;
    if (previous) {
      // Disconnected legs or conflicting night counts are not verified ground truth.
      if (previous.name.toLowerCase() !== tr.from.toLowerCase()
        || previous.departure.getTime() !== tr.date.getTime()) return null;
    } else {
      visits.push({ name: tr.from, departure: tr.date });
    }
    visits.push({ name: tr.to, arrival: tr.date, departure: _addDays(tr.date, tr.nights) });
  }

  const lines = visits.map(visit => {
    const arrival = visit.arrival ? _ymd(visit.arrival) : 'unknown (derive from transport data in the sheet)';
    return `  ${visit.name}: arrivalDate=${arrival}, departureDate=${_ymd(visit.departure)}`;
  });

  return [
    'PRECOMPUTED DESTINATION DATES — authoritative, do not recalculate:',
    'Each line is a separate visit. Keep return visits to the same city separate.',
    ...lines,
    '(Any "unknown" arrivalDate should be inferred from the flight/transport rows in the sheet below.)',
    '',
  ].join('\n');
}

// ── File preparation (stays client-side) ─────────────────────────────────────

type GeminiMime = 'application/pdf' | 'image/jpeg' | 'image/png' | 'image/webp';

const GEMINI_MIME_TYPES: Record<string, GeminiMime> = {
  'application/pdf': 'application/pdf',
  'image/jpeg':      'image/jpeg',
  'image/jpg':       'image/jpeg',
  'image/png':       'image/png',
  'image/webp':      'image/webp',
};

/** Encode an ArrayBuffer as a base64 string. */
function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/**
 * Convert a File into an ItineraryInput ready to send to the Cloud Function.
 *
 * Supported formats:
 *   - Plain text / CSV / TXT  →  text content (UTF-8)
 *   - Excel (.xlsx / .xls)    →  CSV text via SheetJS
 *   - Word (.docx)            →  plain text via mammoth
 *   - PDF                     →  base64 inline data
 *   - Images (JPEG/PNG/WebP)  →  base64 inline data
 */
export async function prepareFileInput(file: File): Promise<ItineraryInput> {
  const name = file.name.toLowerCase();
  if (file.size === 0) throw new Error('The file is empty.');
  if (file.size >= BOOKING_DOC_MAX_BYTES) throw new Error('Each attachment must be smaller than 15 MiB.');
  if (name.endsWith('.doc') || (file.type === 'application/msword' && !name.endsWith('.docx'))) {
    throw new Error('Save legacy Word documents as .docx or PDF before importing.');
  }

  // ── Excel ──────────────────────────────────────────────────────────────────
  if (
    file.type.includes('spreadsheet') ||
    file.type.includes('excel') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.xls')
  ) {
    const XLSX = await import('xlsx');
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer, { type: 'array' });

    const sections = wb.SheetNames.map(sheetName => {
      // Use raw:false so dates/numbers come out as formatted strings
      const rows = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName], {
        header: 1,
        raw: false,
        defval: '',
      }) as string[][];

      // Standalone notes can contain booking evidence too; discard only empty rows.
      const contentRows = rows.filter(
        row => row.some(c => String(c).trim())
      );
      if (contentRows.length === 0) return null;

      // Detect Convention B and pre-compute dates before Gemini sees the text.
      // This bypasses the +1 travel-day hallucination entirely.
      const precomputed = _precomputeConventionB(contentRows);

      // Preserve headers, blank columns and first bookings without guessing row roles.
      // The existing exporter also escapes commas, quotes and multiline evidence.
      // https://docs.sheetjs.com/docs/api/utilities/csv/#delimiter-separated-output
      const body = XLSX.utils.sheet_to_csv(wb.Sheets[sheetName], { blankrows: false });

      if (!body) return null;
      const sheetBlock = `=== Sheet: ${sheetName} ===\n${body}`;
      return precomputed ? `${precomputed}\n${sheetBlock}` : sheetBlock;
    }).filter(Boolean);

    if (sections.length === 0) throw new Error('No data found in Excel file.');
    return { type: 'text', content: sections.join('\n\n') };
  }

  // ── Word documents ─────────────────────────────────────────────────────────
  if (
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    name.endsWith('.docx')
  ) {
    const mammoth = await import('mammoth');
    const buffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer: buffer });
    if (!result.value.trim()) throw new Error('Could not extract text from Word document.');
    return { type: 'text', content: result.value };
  }

  // ── Plain text / CSV ───────────────────────────────────────────────────────
  if (
    file.type === 'text/csv' ||
    file.type === 'text/plain' ||
    name.endsWith('.csv') ||
    name.endsWith('.txt')
  ) {
    const text = await file.text();
    if (!text.trim()) throw new Error('The file contains no text.');
    return { type: 'text', content: text };
  }

  // ── PDF / images — pass as base64 inline data ──────────────────────────────
  const geminiMime = GEMINI_MIME_TYPES[file.type] || GEMINI_MIME_TYPES[itineraryFileType(file)];
  if (geminiMime) {
    const buffer = await file.arrayBuffer();
    return { type: 'file', base64: toBase64(buffer), mimeType: geminiMime };
  }

  throw new Error(`Unsupported file type: ${file.type || file.name}`);
}

/** Decode email for parsing only. The uploaded .eml remains byte-for-byte original. */
export async function prepareFileInputs(file: File, depth = 0): Promise<ItineraryInput[]> {
  if (itineraryFileType(file) !== 'message/rfc822') return [await prepareFileInput(file)];
  if (!file.size || file.size >= BOOKING_DOC_MAX_BYTES) throw new Error('Email must be non-empty and smaller than 15 MiB.');
  if (depth > 3) throw new Error('Too many forwarded emails nested inside each other. Import the inner email separately.');
  const { default: PostalMime } = await import('postal-mime');
  const email = await PostalMime.parse(await file.arrayBuffer(), { rfc822Attachments: true });
  const inputs: ItineraryInput[] = [];
  // Never render or fetch untrusted email HTML while extracting text.
  const { default: sanitizeHtml } = await import('sanitize-html');
  const body = email.text?.trim() || sanitizeHtml((email.html || '').replace(/<\/(?:p|div|tr|li|h[1-6])\s*>|<br\s*\/?\s*>/gi, '\n'), { allowedTags: [], allowedAttributes: {} }).trim();
  if (body) inputs.push({ type: 'text', content: `${email.subject || ''}\n${body}` });
  for (const attachment of email.attachments) {
    // Inline images can be the actual ticket or barcode, not just decoration.
    // Unsupported formats remain available in the unchanged original .eml.
    const extension = attachment.mimeType === 'message/rfc822' ? 'eml' : attachment.mimeType.split('/')[1];
    const name = attachment.filename || `attachment.${extension}`;
    const attached = new File([attachment.content as ArrayBuffer], name, { type: attachment.mimeType });
    try { itineraryFileType(attached); } catch { continue; }
    inputs.push(...await prepareFileInputs(attached, depth + 1));
    if (inputs.length > 20) throw new Error('Too many email attachments. Import fewer files at a time.');
  }
  if (!inputs.length) throw new Error('No readable email body or supported attachments.');
  return inputs;
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Thrown when the Gemini daily quota is exhausted. */
export class QuotaExhaustedError extends Error {
  constructor() {
    super('quota-exhausted');
    this.name = 'QuotaExhaustedError';
  }
}

/**
 * Parse one or more file inputs into a structured ParsedItinerary.
 * Delegates to the `parseItinerary` Cloud Function — the Gemini key stays
 * server-side.
 *
 * Throws `QuotaExhaustedError` when the daily Gemini quota is exhausted so
 * callers can surface a targeted message instead of a generic error.
 */
export async function parseItinerary(
  inputs: ItineraryInput[],
  homeCurrency?: string
): Promise<ParsedItinerary | null> {
  try {
    const result = await _parseItineraryCf({ inputs, homeCurrency });
    return result.data ?? null;
  } catch (err: any) {
    // Firebase callable functions surface HttpsError codes in err.code
    if (err?.code === 'functions/resource-exhausted') {
      throw new QuotaExhaustedError();
    }
    console.error('parseItinerary (CF) failed:', err);
    return null;
  }
}

/**
 * Generate { emoji, blurb } for each destination at save time.
 * Returns a 📍 / empty-blurb fallback per destination on failure — never throws.
 */
export async function generateDestinationDetails(
  destinations: { city: string; country?: string | null }[]
): Promise<{ emoji: string; blurb: string }[]> {
  const fallback = destinations.map(() => ({ emoji: '📍', blurb: '' }));
  if (destinations.length === 0) return [];
  try {
    const result = await _generateDestinationDetailsCf({ destinations });
    return result.data ?? fallback;
  } catch (err) {
    console.warn('generateDestinationDetails (CF) failed:', err);
    return fallback;
  }
}

/**
 * Generate an inspiring trip name from destination info.
 * Delegates to the `generateTripName` Cloud Function.
 * Returns null on failure so callers can surface the error appropriately.
 */
export async function generateInspiringTripName(
  destinations: { city: string; country?: string }[]
): Promise<string | null> {
  try {
    const result = await _generateTripNameCf({ destinations });
    return result.data ?? null;
  } catch (err) {
    console.warn('generateInspiringTripName (CF) failed:', err);
    return null;
  }
}
